import type { Clock } from '@obraplan/core/application/ports/clock';
import type { ApiResponseRow, ObraPlanDb } from '@/infrastructure/persistence/db';
import type { ConnectionStatus } from './connection-status';

/**
 * A leitura guardada para quando faltar sinal (Fase 43, RN-597, ADR-192).
 *
 * É um `fetch` que envolve o `fetch`. O `api-client.ts` continua igual: recebe uma `Response`,
 * lê o texto e o passa pelo `valueObjectReviver`. A diferença é que, sem rede, a `Response`
 * pode ser a que este arquivo guardou da última vez — e a tela não percebe a troca.
 *
 * ## O que é guardado
 *
 * Só leitura: `Get…` e `List…`, menos `GetOrOpen…` e `GetOrCreate…`, que gravam. E só a
 * resposta de sucesso, com o corpo cru, como o servidor o escreveu.
 *
 * ## De quem é
 *
 * A chave começa pelo hash do token. Duas pessoas no mesmo tablet não se leem: cada sessão só
 * alcança o que ela mesma abriu, e sair apaga o que era dela. O corpo entra na chave em forma
 * canônica (chaves em ordem), para a pré-carga e a tela acharem a mesma leitura mesmo que
 * escrevam o objeto em outra ordem.
 *
 * ## Quando o guardado é entregue
 *
 * - **Não houve resposta** — o `fetch` rejeitou: rede caída, DNS, avião.
 * - **A resposta não era a nossa** — veio HTML, e não JSON: o portal do Wi-Fi da obra, o 502 do
 *   proxy com a API fora. O `api-client` chamaria isso de "resposta ilegível"; para quem está no
 *   pavimento é o mesmo que não ter sinal.
 * - **A rede não respondeu em `slowNetworkMs`** — sinal de uma barra, que não cai e não chega.
 *   Só quando há guardado: sem ele, esperar é a única coisa a fazer.
 *
 * Resposta do servidor nunca é trocada: a recusa de sessão (401) continua recusa, e apaga o
 * guardado daquela sessão — o token morreu, e o que ele leu não serve a mais ninguém.
 */

/** Sete dias (RN-597): uma semana de obra, e nada de dado de mês passado fingindo ser de hoje. */
export const KEEP_FOR_MS = 7 * 24 * 60 * 60 * 1000;

/** Quanto esperar a rede quando há leitura guardada para entregar no lugar. */
export const SLOW_NETWORK_MS = 8000;

/** Marca a resposta que saiu do aparelho, com a hora em que foi guardada. */
export const SERVED_FROM_DEVICE_HEADER = 'x-obraplan-guardado';

export interface ResponseStore {
  get(key: string): Promise<ApiResponseRow | undefined>;
  put(row: ApiResponseRow): Promise<void>;
  forgetNamespace(namespace: string): Promise<void>;
  forgetOlderThan(iso: string): Promise<void>;
}

export class DexieResponseStore implements ResponseStore {
  constructor(private readonly db: ObraPlanDb) {}

  get(key: string): Promise<ApiResponseRow | undefined> {
    return this.db.apiResponses.get(key);
  }

  async put(row: ApiResponseRow): Promise<void> {
    await this.db.apiResponses.put(row);
  }

  async forgetNamespace(namespace: string): Promise<void> {
    await this.db.apiResponses.where('namespace').equals(namespace).delete();
  }

  async forgetOlderThan(iso: string): Promise<void> {
    await this.db.apiResponses.where('savedAt').below(iso).delete();
  }
}

/** `/api/get-daily-inspections` sim; `/api/get-or-open-daily-report` não, porque grava. */
export function isCachedRead(path: string): boolean {
  return /^\/api\/(get|list)-/.test(path) && !/^\/api\/get-or-/.test(path);
}

/** JSON com as chaves em ordem, em qualquer profundidade. */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(ordenar(value));
}

function ordenar(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(ordenar);
  if (value === null || typeof value !== 'object') return value;
  const entradas = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return Object.fromEntries(entradas.map(([k, v]) => [k, ordenar(v)]));
}

/** O hash do token, em hexadecimal: é o nome da gaveta da sessão, e o token não fica escrito. */
export async function namespaceFor(token: string): Promise<string> {
  const bytes = new TextEncoder().encode(token);
  const resumo = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(resumo)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface OfflineFetchOptions {
  readonly fetch: typeof globalThis.fetch;
  readonly store: ResponseStore;
  readonly clock: Clock;
  readonly status: ConnectionStatus;
  readonly slowNetworkMs?: number;
  /** O `navigator.onLine`: com ele em `false`, a leitura guardada sai sem esperar a rede. */
  readonly isOnline?: () => boolean;
}

export function createOfflineFetch(options: OfflineFetchOptions): typeof globalThis.fetch {
  const { store, clock, status } = options;
  const buscar = options.fetch;
  const prazo = options.slowNetworkMs ?? SLOW_NETWORK_MS;
  const temRede = options.isOnline ?? (() => true);
  let faxinaFeita = false;

  /** O guardado de mais de sete dias sai uma vez por carga da página, sem segurar ninguém. */
  const faxina = (): void => {
    if (faxinaFeita) return;
    faxinaFeita = true;
    const limite = new Date(clock.now().getTime() - KEEP_FOR_MS).toISOString();
    void store.forgetOlderThan(limite).catch(() => undefined);
  };

  /** A chamada que não é leitura: passa direto, e só conta se houve resposta. */
  const passar = async (url: string, init: RequestInit | undefined): Promise<Response> => {
    try {
      const resposta = await buscar(url, init);
      if (respondeuONosso(resposta)) status.markOnline();
      else status.markOffline();
      return resposta;
    } catch (causa) {
      status.markOffline();
      throw causa;
    }
  };

  return async (entrada: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof entrada === 'string' ? entrada : entrada instanceof URL ? entrada.href : entrada.url;
    const caminho = new URL(url, 'http://aparelho').pathname;
    const token = tokenDe(init?.headers);
    if (!isCachedRead(caminho) || token === undefined) return passar(url, init);

    faxina();
    const gaveta = await namespaceFor(token);
    const chave = `${gaveta}|${caminho}|${corpoCanonico(init?.body)}`;
    const guardada = await store.get(chave).catch(() => undefined);

    const entregar = (linha: ApiResponseRow): Response => {
      status.markOffline(linha.savedAt);
      return new Response(linha.text, {
        status: 200,
        headers: { 'content-type': 'application/json', [SERVED_FROM_DEVICE_HEADER]: linha.savedAt },
      });
    };

    if (guardada !== undefined && !temRede()) return entregar(guardada);

    let resposta: Response;
    try {
      resposta = await (guardada === undefined
        ? buscar(url, init)
        : comPrazo(buscar, url, init, prazo));
    } catch (causa) {
      if (guardada !== undefined) return entregar(guardada);
      status.markOffline();
      throw causa;
    }

    if (!ehNossa(resposta)) {
      if (guardada !== undefined) return entregar(guardada);
      if (respondeuONosso(resposta)) status.markOnline();
      else status.markOffline();
      return resposta;
    }

    status.markOnline();
    if (resposta.ok) {
      const texto = await resposta.clone().text();
      void store
        .put({ key: chave, namespace: gaveta, text: texto, savedAt: clock.nowIso() })
        .catch(() => undefined);
    } else if (resposta.status === 401) {
      void store.forgetNamespace(gaveta).catch(() => undefined);
    }
    return resposta;
  };
}

/** A resposta é da API quando vem em JSON; HTML é de quem respondeu no lugar dela. */
function ehNossa(resposta: Response): boolean {
  return (resposta.headers.get('content-type') ?? '').includes('application/json');
}

/**
 * Houve conexão com o servidor, ainda que a resposta não seja a nossa: o `413` vem do nginx, na
 * frente da API, e quem o recebe está com sinal — só mandou um arquivo grande demais.
 */
function respondeuONosso(resposta: Response): boolean {
  return ehNossa(resposta) || resposta.status === 413;
}

function tokenDe(headers: HeadersInit | undefined): string | undefined {
  if (headers === undefined) return undefined;
  const valor = new Headers(headers).get('authorization');
  if (valor === null || !valor.startsWith('Bearer ')) return undefined;
  return valor.slice('Bearer '.length);
}

function corpoCanonico(corpo: BodyInit | null | undefined): string {
  if (typeof corpo !== 'string') return '{}';
  try {
    return canonicalJson(JSON.parse(corpo));
  } catch {
    return corpo;
  }
}

/** A rede, com prazo: estourado, a requisição é cancelada e quem chamou recebe a rejeição. */
async function comPrazo(
  buscar: typeof globalThis.fetch,
  url: string,
  init: RequestInit | undefined,
  ms: number,
): Promise<Response> {
  const controle = new AbortController();
  const relogio = setTimeout(() => controle.abort(), ms);
  try {
    return await buscar(url, { ...init, signal: controle.signal });
  } finally {
    clearTimeout(relogio);
  }
}
